import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, Divider, Snackbar } from '@mui/material';
import useTrackPageView from '../../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../../constants/gaEnums';
import { useParams, useNavigate } from 'react-router-dom';

import { useUserProfile, NICKNAME_MAX_LENGTH, BIO_MAX_LENGTH } from '../../../hooks/useUserProfile';
import { adminApi } from '../../../api/admin';
import { meApi } from '../../../api/me';

import ProgressBar from '../../../components/common/ProgressBar';
import OpenInAppBanner from '../../../components/common/OpenInAppBanner';
import ProfileAvatar from '../components/ProfileAvatar';
import DeleteConfirmDialog from '../../../components/dialog/DeleteConfirmDialog';
import UserSuspensionDialog from '../../../components/dialog/UserSuspensionDialog';
import ForceWithdrawDialog from '../../../components/dialog/ForceWithdrawDialog';
import GradeChangeDialog from '../../../components/dialog/GradeChangeDialog';
import TabButton from '../../../components/button/TabButton';
import SidebarLayout from '../../../components/layout/SidebarLayout';
import ReviewTab from './ReviewTab';
import ListTab from './ListTab';
import CommentTab from './CommentTab';
import SavedTab from './SavedTab';
import { usersApi } from '../../../api/users';
import CancelConfirmDialog from '../../../components/dialog/CancelConfirmDialog';
import PageLayout from '../../../components/layout/PageLayout';
import BasicButton from '../../../components/button/BasicButton';
import CharacterCounter from '../../../components/common/CharacterCounter';
import ProfilePageActions from '../components/ProfilePageActions';
import NicknameField from '../components/NicknameField';
import BioField from '../components/BioField';
import FollowListDialog from '../components/FollowListDialog';
import CustomMenu from '../../../components/common/CustomMenu';
import ActionDialog from '../../../components/dialog/ActionDialog';
import theme from '../../../theme';
import { saveCurrentPathAndRedirectToLogin } from '../../../utils/authUtils';
import { shareOrCopyUrl } from '../../../utils/share';

const UserProfilePage = ({ nickname, legacyTab }) => {
  const { tab } = useParams();
  const tabFromUrl = legacyTab || tab;
  const navigate = useNavigate();
  const {
    user,
    loading,
    viewMode,
    isEditing,
    setIsEditing,
    profileForm,
    isSuspensionDialogOpen,
    isDeleteDialogOpen,
    isReactivationDialogOpen,
    isMyImageDeleteDialogOpen,
    setIsSuspensionDialogOpen,
    setIsDeleteDialogOpen,
    setIsReactivationDialogOpen,
    setIsMyImageDeleteDialogOpen,
    imageMenuAnchor,
    setImageMenuAnchor,
    handleProfileInputChange,
    handleProfileSubmit,
    handleCancelEdit,
    handleLogout,
    handleImageUpload,
    handleMyImageDelete,
    handleConfirmMyImageDelete,
    handleSuspend,
    handleConfirmSuspend,
    handleUnsuspend,
    handleConfirmUnsuspend,
    handleDeleteProfileImage,
    handleConfirmDelete,
    handleConfirmGradeChange,
    userError,
    setUserError,
    setProfileUser,
  } = useUserProfile(nickname);
  const fileInputRef = useRef(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [memberSettingAnchor, setMemberSettingAnchor] = useState(null);
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [isWithdrawSuccessDialogOpen, setIsWithdrawSuccessDialogOpen] = useState(false);
  const [isRoleChangeDialogOpen, setIsRoleChangeDialogOpen] = useState(false);
  const [isForceWithdrawDialogOpen, setIsForceWithdrawDialogOpen] = useState(false);
  const [isForceWithdrawSuccessDialogOpen, setIsForceWithdrawSuccessDialogOpen] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [followDialogOpen, setFollowDialogOpen] = useState(false);
  const [followDialogTab, setFollowDialogTab] = useState('followers');
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset the input value so the same file can be selected again
    event.target.value = null;
  };

  const handleWithdraw = async () => {
    try {
      await meApi.withdraw();
      setIsWithdrawDialogOpen(false);
      setIsWithdrawSuccessDialogOpen(true);
    } catch (error) {
      console.error('회원 탈퇴 실패:', error);
    }
  };

  const handleWithdrawSuccess = () => {
    setIsWithdrawSuccessDialogOpen(false);
    handleLogout();
  };

  // 강제 탈퇴 (관리자)
  const handleForceWithdraw = async (reason) => {
    try {
      await adminApi.withdrawUsers([user?.id], reason);
      setIsForceWithdrawDialogOpen(false);
      setIsForceWithdrawSuccessDialogOpen(true);
    } catch (error) {
      console.error('강제 탈퇴 실패:', error);
    }
  };

  const handleForceWithdrawSuccess = () => {
    setIsForceWithdrawSuccessDialogOpen(false);
    navigate('/admin?mainTab=user&subTab=withdrawn');
  };

  // 등급 변경 (관리자)
  const handleGradeChange = async (grade) => {
    const success = await handleConfirmGradeChange(grade);
    if (success) {
      setIsRoleChangeDialogOpen(false);
    }
  };

  const isMyPage = viewMode === 'myPage';
  const isAdminView = viewMode === 'admin';
  const isPublicView = viewMode === 'public';

  useTrackPageView(
    {
      pageType: PAGE_TYPE.MYPAGE,
      ...(!isMyPage && {
        contentType: CONTENT_TYPE.USER_PROFILE,
        contentId: user?.id,
        contentIdPrefix: 'user',
        contentName: user?.nickname,
      }),
    },
    { ready: !!user?.id },
  );

  // 사진 등록 메뉴 아이템
  const imageMenuItems = [
    {
      text: '사진 등록',
      onClick: () => fileInputRef.current?.click(),
    },
    {
      text: '사진 삭제',
      onClick: handleMyImageDelete,
    },
  ];

  // 프로필 설정 메뉴 아이템
  const profileMenuItems = [
    {
      text: '정보 수정',
      onClick: () => {
        setIsEditing(true);
        setAnchorEl(null);
      },
    },
  ];

  // 비밀번호 변경 메뉴 아이템 (조건부)
  if (isMyPage && !user?.provider) {
    profileMenuItems.push({
      text: '비밀번호 변경',
      onClick: () => {
        navigate('/password-change');
        setAnchorEl(null);
      },
    });
  }

  if (isMyPage) {
    profileMenuItems.push({
      text: '로그아웃',
      onClick: () => {
        handleLogout();
        setAnchorEl(null);
      },
    });
  }

  // 회원 탈퇴 메뉴 아이템 (마이페이지에서만)
  if (isMyPage) {
    profileMenuItems.push({
      text: '회원 탈퇴',
      onClick: () => {
        setIsWithdrawDialogOpen(true);
        setAnchorEl(null);
      },
    });
  }

  const profileUrl = `/users/by-id/${user?.id}`;
  const handleProfileShare = async () => {
    if (!user?.id) return;
    try {
      const result = await shareOrCopyUrl({
        url: profileUrl,
        title: `${user?.nickname || nickname || 'postpop'}님의 음악 기록`,
        text: `${user?.nickname || nickname || 'postpop'}님의 음악 기록`,
      });
      if (result === 'copied') {
        setShareSnackbarOpen(true);
      }
    } catch (err) {
      console.error('프로필 공유 실패:', err);
    }
  };

  const handleFollowToggle = async () => {
    if (!user?.id || isFollowLoading) return;
    const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    if (!token) {
      saveCurrentPathAndRedirectToLogin(navigate);
      return;
    }

    setIsFollowLoading(true);
    try {
      const response = user.isFollowing
        ? await usersApi.unfollowUser(user.id)
        : await usersApi.followUser(user.id);
      const updatedUser = response.data;
      setProfileUser((prev) => ({ ...(prev || user), ...updatedUser }));
    } catch (error) {
      console.error('팔로우 상태 변경 실패:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleFollowStatsClick = (tab) => {
    if (!localStorage.getItem('POSTPOP_AUTH_TOKEN')) {
      saveCurrentPathAndRedirectToLogin(navigate);
      return;
    }
    setFollowDialogTab(tab);
    setFollowDialogOpen(true);
  };

  const followButtonTone = user?.isFollowing
    ? {
        bg: 'var(--pp-surface)',
        fg: 'var(--pp-text-primary)',
        border: 'var(--pp-border)',
      }
    : {
        bg: 'var(--pp-button-primary-bg)',
        fg: 'var(--pp-button-primary-fg)',
        border: 'var(--pp-button-primary-bg)',
      };

  const renderFollowShareButtons = ({ mobile = false } = {}) => (
    <Box sx={{ display: 'flex', gap: 1, width: mobile ? '100%' : 'auto' }}>
      <Box
        component="button"
        type="button"
        onClick={handleFollowToggle}
        disabled={isFollowLoading}
        sx={{
          appearance: 'none',
          boxSizing: 'border-box',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: mobile ? 1 : 'initial',
          width: mobile ? 'auto' : '130px',
          height: { xs: '40px', md: '38px', lg: '40px' },
          padding: { xs: '12px 16px', md: '12px 20px', lg: '16px 12px' },
          m: 0,
          borderRadius: '4px',
          typography: 'body3',
          fontWeight: 'medium',
          lineHeight: 1,
          boxShadow: 'none',
          cursor: isFollowLoading ? 'default' : 'pointer',
          color: `${followButtonTone.fg} !important`,
          WebkitTextFillColor: `${followButtonTone.fg} !important`,
          backgroundColor: followButtonTone.bg,
          border: `1px solid ${followButtonTone.border}`,
          transition: 'none',
          opacity: 1,
          '&:hover, &:focus, &:active, &:disabled': {
            backgroundColor: followButtonTone.bg,
            border: `1px solid ${followButtonTone.border}`,
            color: `${followButtonTone.fg} !important`,
            WebkitTextFillColor: `${followButtonTone.fg} !important`,
            outline: 'none',
          },
          '&, & *': {
            color: `${followButtonTone.fg} !important`,
            WebkitTextFillColor: `${followButtonTone.fg} !important`,
          },
        }}
      >
        {user?.isFollowing ? '팔로우 중' : '팔로우'}
      </Box>
      <BasicButton
        variant="outlined"
        onClick={handleProfileShare}
        sx={{
          flex: mobile ? 1 : 'initial',
          width: mobile ? 'auto' : '130px',
          height: { xs: '40px', md: '38px', lg: '40px' },
          padding: { xs: '12px 16px', md: '12px 20px', lg: '16px 12px' },
          typography: 'body3',
          fontWeight: 'medium',
        }}
      >
        프로필 공유
      </BasicButton>
    </Box>
  );

  const renderProfileActions = ({ mobile = false } = {}) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: mobile ? '100%' : 'auto',
      }}
    >
      {!isMyPage && renderFollowShareButtons({ mobile })}
      {(isMyPage || isAdminView) && (
        <ProfilePageActions
          variant={mobile ? 'mobile' : 'tablet'}
          isMyPage={isMyPage}
          isAdminView={isAdminView}
          handleProfileSettingClick={(e) => setAnchorEl(e.currentTarget)}
          handleMemberSettingClick={(e) => setMemberSettingAnchor(e.currentTarget)}
          handleEditClick={() => setIsEditing(true)}
          handleProfileShare={handleProfileShare}
        />
      )}
    </Box>
  );

  // 회원 설정 메뉴 아이템 (관리자 뷰에서만)
  const memberSettingMenuItems = [
    {
      text: '등급 변경',
      onClick: () => {
        setIsRoleChangeDialogOpen(true);
        setMemberSettingAnchor(null);
      },
    },
    user?.isActive
      ? {
          text: '활동 정지',
          onClick: () => {
            handleSuspend();
            setMemberSettingAnchor(null);
          },
        }
      : {
          text: '활동 정지 해제',
          onClick: () => {
            handleUnsuspend();
            setMemberSettingAnchor(null);
          },
        },
    {
      text: '강제 탈퇴',
      onClick: () => {
        setIsForceWithdrawDialogOpen(true);
        setMemberSettingAnchor(null);
      },
    },
  ];

  const TABS = useMemo(
    () => [
      {
        id: 'reviews',
        label: '나의 리뷰',
        component: (
          <ReviewTab
            api={
              isMyPage
                ? meApi.getMyReviews
                : isAdminView
                  ? adminApi.getUserReviews
                  : usersApi.getUserReviews
            }
            userId={isAdminView ? user?.id : nickname}
          />
        ),
      },
      {
        id: 'lists',
        label: '나의 플리',
        component: (
          <ListTab
            api={
              isMyPage
                ? meApi.getMyLists
                : isAdminView
                  ? adminApi.getUserLists
                  : usersApi.getUserLists
            }
            userId={isAdminView ? user?.id : nickname}
          />
        ),
      },
      {
        id: 'comments',
        label: '나의 댓글',
        component: (
          <CommentTab
            api={
              isMyPage
                ? meApi.getMyComments
                : isAdminView
                  ? adminApi.getUserComments
                  : usersApi.getUserComments
            }
            userId={isAdminView ? user?.id : nickname}
          />
        ),
      },
      {
        id: 'saved',
        label: '저장한 게시물',
        component: (
          <SavedTab
            api={
              isMyPage
                ? meApi.getMyBookmarks
                : isAdminView
                  ? adminApi.getUserBookmarks
                  : usersApi.getUserBookmarks
            }
            userId={isAdminView ? user?.id : nickname}
            readOnly={isPublicView || isAdminView}
          />
        ),
      },
    ],
    [isMyPage, isAdminView, isPublicView, nickname, user?.id],
  );

  const validTabIds = useMemo(() => TABS.map((t) => t.id), [TABS]);

  const activeTab = useMemo(() => {
    return tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : 'reviews';
  }, [tabFromUrl, validTabIds]);

  const handleTabChange = (newTab) => {
    const path = isMyPage
      ? `/mypage/${newTab}`
      : `/users/${encodeURIComponent(nickname)}/${newTab}`;
    navigate(path);
  };

  if (loading) return <ProgressBar isLoading={true} />;
  if (!user)
    return (
      <PageLayout>
        <ActionDialog
          open={!!userError}
          onClose={() => {
            setUserError(null);
            navigate('/', { replace: true });
          }}
          onConfirm={() => {
            setUserError(null);
            navigate('/', { replace: true });
          }}
          showCancel={false}
        >
          <Typography sx={{ textAlign: 'center' }}>{userError}</Typography>
        </ActionDialog>
      </PageLayout>
    );

  const currentTab = TABS.find((tab) => tab.id === activeTab);

  return (
    <PageLayout>
      <OpenInAppBanner type="profile" title={user.nickname} image={user.profileImageUrl} />
      {/* 프로필 섹션 */}
      <Box sx={{ mb: { xs: 5, md: 0 } }}>
        <Box sx={{ display: { xs: 'block', lg: 'flex' }, gap: 3.75 }}>
          {/* 좌측 영역 */}
          <Box sx={{ width: '100%' }}>
            {/* 상단 (프로필 사진 + 닉네임) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  position: 'relative',
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 },
                  pr: {
                    xs: 0,
                    md: !isEditing && (isMyPage || isAdminView || isPublicView) ? '284px' : 0,
                  },
                }}
              >
                <Box>
                  <ProfileAvatar
                    src={user.profileImageUrl}
                    alt={user.nickname}
                    size={{ xs: 56, md: 60, lg: 64 }}
                    isMyPage={isMyPage}
                    onDelete={isAdminView ? handleDeleteProfileImage : undefined}
                    onMenuOpen={isMyPage ? (e) => setImageMenuAnchor(e.currentTarget) : undefined}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/png, image/jpeg"
                    onChange={handleFileSelect}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    minHeight: { xs: 56, md: 60, lg: 64 },
                  }}
                >
                  <NicknameField
                    isEditing={isEditing}
                    profileForm={profileForm}
                    handleProfileInputChange={handleProfileInputChange}
                    grade={user?.grade}
                  />
                  {!isEditing && (
                    <Typography
                      sx={{
                        mt: { xs: 0.5, md: 0.75 },
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: theme.palette.black[3],
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, md: 1.25 },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleFollowStatsClick('followers')}
                        sx={{
                          p: 0,
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                        }}
                      >
                        팔로워 {user?.followersCount || 0}
                      </Box>
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          width: '1px',
                          height: { xs: '10px', md: '11px' },
                          bgcolor: theme.palette.grey[500],
                          alignSelf: 'center',
                        }}
                      />
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleFollowStatsClick('following')}
                        sx={{
                          p: 0,
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                        }}
                      >
                        팔로잉 {user?.followingCount || 0}
                      </Box>
                    </Typography>
                  )}
                </Box>
                {!isEditing && (isMyPage || isAdminView || isPublicView) && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      position: 'absolute',
                      right: 0,
                      top: { md: '11px', lg: '12px' },
                    }}
                  >
                    {renderProfileActions()}
                  </Box>
                )}
              </Box>
              <CharacterCounter
                currentLength={profileForm?.nickname?.length || 0}
                maxLength={NICKNAME_MAX_LENGTH}
                sx={{
                  mt: isEditing ? 1 : 0,
                  mb: { xs: 2, md: isEditing ? 2.5 : 3.75, lg: 2.5 },
                  visibility: isEditing ? 'visible' : 'hidden',
                  height: isEditing ? 'auto' : '0px',
                }}
              />
            </Box>
            {/* 하단 (자기소개) */}
            <Box>
              <BioField
                isEditing={isEditing}
                profileForm={profileForm}
                handleProfileInputChange={handleProfileInputChange}
              />
              {!isEditing && (isMyPage || isAdminView || isPublicView) && (
                <Box sx={{ mt: { xs: 3, md: 3.5 }, display: { xs: 'flex', md: 'none' } }}>
                  {renderProfileActions({ mobile: true })}
                </Box>
              )}
              {isEditing && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { md: 'space-between' },
                    alignItems: { xs: 'flex-end', md: 'center' },
                    mt: 1,
                  }}
                >
                  <CharacterCounter
                    currentLength={profileForm?.bio?.length || 0}
                    maxLength={BIO_MAX_LENGTH}
                    sx={{ mb: { xs: 2, md: 0 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                    <BasicButton
                      variant="outlined"
                      onClick={() => setIsCancelDialogOpen(true)}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        minWidth: { md: '66px', lg: '58px' },
                        typography: 'body3',
                        fontWeight: 'medium',
                        height: { xs: '40px', md: '38px', lg: '30px' },
                        padding: { xs: '12px 16px', md: '12px 20px', lg: '8px 16px' },
                      }}
                    >
                      취소
                    </BasicButton>
                    <BasicButton
                      onClick={handleProfileSubmit}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        minWidth: { md: '66px', lg: '58px' },
                        typography: 'body3',
                        fontWeight: 'medium',
                        color: 'white',
                        height: { xs: '40px', md: '38px', lg: '30px' },
                        padding: { xs: '12px 16px', md: '12px 20px', lg: '8px 16px' },
                      }}
                    >
                      확인
                    </BasicButton>
                  </Box>
                </Box>
              )}
            </Box>

          </Box>
        </Box>
      </Box>

      {/* 메뉴 */}
      {isMyPage && (
        <CustomMenu
          anchorEl={imageMenuAnchor}
          open={Boolean(imageMenuAnchor)}
          onClose={() => setImageMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          items={imageMenuItems}
        />
      )}
      <CustomMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        items={profileMenuItems}
      />
      {isAdminView && (
        <CustomMenu
          anchorEl={memberSettingAnchor}
          open={Boolean(memberSettingAnchor)}
          onClose={() => setMemberSettingAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          items={memberSettingMenuItems}
        />
      )}

      <Divider
        sx={{
          mt: { xs: 0, md: 4, lg: 4.5 },
          mx: { xs: -2.5, md: 0 },
          display: 'block',
          mb: { xs: 5, md: 4 },
          borderColor: theme.palette.grey.line,
        }}
      />

      {/* 탭 섹션 */}
      <SidebarLayout pageType={PAGE_TYPE.MYPAGE}>
        <TabButton
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={TABS}
          centered={false}
          marginTop={{ xs: '30px', md: '20px', lg: '30px' }}
        >
          {currentTab?.component}
        </TabButton>
      </SidebarLayout>

      {/* 다이얼로그 */}
      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        message={`${user.nickname}님의 프로필 사진을 삭제하시겠습니까?`}
      />
      <UserSuspensionDialog
        open={isSuspensionDialogOpen}
        onClose={() => setIsSuspensionDialogOpen(false)}
        onConfirm={handleConfirmSuspend}
      />
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={() => {
          handleCancelEdit();
          setIsCancelDialogOpen(false);
        }}
      />
      <DeleteConfirmDialog
        open={isMyImageDeleteDialogOpen}
        onClose={() => setIsMyImageDeleteDialogOpen(false)}
        onConfirm={handleConfirmMyImageDelete}
      />
      <ActionDialog
        open={isReactivationDialogOpen}
        onClose={() => setIsReactivationDialogOpen(false)}
        onConfirm={handleConfirmUnsuspend}
        confirmText="변경"
      >
        <Typography sx={{ textAlign: 'center' }}>
          선택된 회원을 활동 멤버로 변경하시겠습니까?
        </Typography>
      </ActionDialog>
      {/* 회원 탈퇴 확인 다이얼로그 */}
      <ActionDialog
        open={isWithdrawDialogOpen}
        onClose={() => setIsWithdrawDialogOpen(false)}
        onConfirm={handleWithdraw}
        confirmText={'회원 탈퇴'}
      >
        <Typography sx={{ textAlign: 'center', lineHeight: 1.5 }}>
          탈퇴하면 계정과 리뷰·댓글 등 활동 정보가 삭제 또는 익명화되며 복구할 수 없습니다.
          <br />
          <br />
          결제·환불 등 별도 보관이 필요한 정보는 개인정보 처리방침에 따라 보관됩니다.
          <br />
          정기후원 자동 갱신은 스토어에서 별도로 관리해 주세요.
          <br />
          <br />
          정말로 탈퇴하시겠습니까?
        </Typography>
      </ActionDialog>
      {/* 회원 탈퇴 완료 다이얼로그 */}
      <ActionDialog
        open={isWithdrawSuccessDialogOpen}
        onClose={handleWithdrawSuccess}
        onConfirm={handleWithdrawSuccess}
        showCancel={false}
      >
        <Typography sx={{ textAlign: 'center' }}>회원 탈퇴가 완료되었습니다.</Typography>
      </ActionDialog>
      {/* 등급 변경 다이얼로그 */}
      <GradeChangeDialog
        open={isRoleChangeDialogOpen}
        onClose={() => setIsRoleChangeDialogOpen(false)}
        onConfirm={handleGradeChange}
        currentGrade={user?.grade}
      />
      {/* 강제 탈퇴 다이얼로그 */}
      <ForceWithdrawDialog
        open={isForceWithdrawDialogOpen}
        onClose={() => setIsForceWithdrawDialogOpen(false)}
        onConfirm={handleForceWithdraw}
      />
      {/* 강제 탈퇴 완료 다이얼로그 */}
      <ActionDialog
        open={isForceWithdrawSuccessDialogOpen}
        onClose={handleForceWithdrawSuccess}
        onConfirm={handleForceWithdrawSuccess}
        showCancel={false}
      >
        <Typography sx={{ textAlign: 'center' }}>회원 탈퇴가 완료되었습니다.</Typography>
      </ActionDialog>
      <FollowListDialog
        open={followDialogOpen}
        onClose={() => setFollowDialogOpen(false)}
        nickname={user?.nickname || nickname || ''}
        followersCount={user?.followersCount || 0}
        followingCount={user?.followingCount || 0}
        initialTab={followDialogTab}
        onProfileUserUpdate={setProfileUser}
      />
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setShareSnackbarOpen(false)}
        message="공유 문구와 링크가 복사되었습니다."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageLayout>
  );
};

export default UserProfilePage;
